import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About & Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Visit Us" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200/60">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
            <span className="grid place-items-center w-10 h-10 rounded-full bg-blue-900 text-white font-display text-lg">
              N
            </span>
            <span className="leading-tight">
              <span className="block font-display text-xl text-slate-900">Nidaan Dental Care</span>
              <span className="block text-[11px] tracking-[0.18em] uppercase text-sky-600">
                Gentle · Modern · Trusted
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm transition-colors duration-200 ${
                    isActive ? "text-sky-600" : "text-slate-600 hover:text-blue-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {settings.phone && (
              <a
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                data-testid="header-phone-link"
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-sky-600 transition-colors duration-200"
              >
                <Phone className="w-4 h-4 text-sky-500" />
                {settings.phone}
              </a>
            )}
            {user ? (
              <>
                <Link to="/my-bookings">
                  <Button variant="ghost" className="rounded-full" data-testid="header-my-bookings-btn">
                    My Bookings
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleLogout}
                  data-testid="header-logout-btn"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <Link to="/signin">
                <Button variant="ghost" className="rounded-full" data-testid="header-signin-btn">
                  Sign In
                </Button>
              </Link>
            )}
            <Link to="/book">
              <Button
                className="rounded-full bg-blue-900 text-white hover:bg-blue-950 hover:text-white px-6"
                data-testid="header-book-btn"
              >
                Book Appointment
              </Button>
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-slate-800"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block text-slate-700"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/my-bookings" onClick={() => setOpen(false)} className="block text-slate-700">
                My Bookings
              </Link>
              <button onClick={handleLogout} className="block text-slate-700">
                Sign Out
              </button>
            </>
          ) : (
            <Link to="/signin" onClick={() => setOpen(false)} className="block text-slate-700">
              Sign In
            </Link>
          )}
          <Link to="/book" onClick={() => setOpen(false)}>
            <Button className="w-full rounded-full bg-blue-900 text-white hover:bg-blue-950 hover:text-white">
              Book Appointment
            </Button>
          </Link>
        </div>
      )}
    </header>
  );
};
